import { z } from 'zod';

import type { ValidateHandler, ValidationErrors } from '$lib/forms';

export const CartographerSLAMServiceAttributesSchema = z
  .object({
    config_params: z
      .object({
        mode: z.string().optional(),
        min_range_meters: z.string().optional(),
        max_range_meters: z.string().optional(),
      })
      .passthrough()
      .optional(),
    camera: z
      .object({
        name: z.string().optional(),
        data_frequency_hz: z.string().optional(),
      })
      .passthrough()
      .optional(),
    movement_sensor: z
      .object({
        name: z.string().optional(),
        data_frequency_hz: z.string().optional(),
      })
      .passthrough()
      .optional(),
    enable_mapping: z.boolean().optional(),
    use_cloud_slam: z.boolean().optional(),
    existing_map: z.string().optional(),
  })
  .passthrough();

export type CartographerSLAMServiceAttributes = z.infer<
  typeof CartographerSLAMServiceAttributesSchema
>;

export const validateCartographerFields: ValidateHandler<
  CartographerSLAMServiceAttributes
> = (values) => {
  const errors: ValidationErrors<CartographerSLAMServiceAttributes> = {};

  const validateNumericField = (
    value: string | undefined,
    fieldKey: keyof CartographerSLAMServiceAttributes,
    errorMessage: string
  ) => {
    if (value) {
      const parsedValue = Number.parseFloat(value);
      if (Number.isNaN(parsedValue) || parsedValue < 0) {
        errors[fieldKey] = errorMessage;
      }
    }
  };

  validateNumericField(
    values.camera?.data_frequency_hz,
    'cameraCaptureRate',
    'Camera data capture rate must be a valid non-negative number'
  );

  validateNumericField(
    values.movement_sensor?.data_frequency_hz,
    'movementSensorCaptureRate',
    'Movement sensor capture rate must be a valid non-negative number'
  );

  validateNumericField(
    values.config_params?.min_range_meters,
    'minRange',
    'Minimum range must be a valid non-negative number'
  );

  validateNumericField(
    values.config_params?.max_range_meters,
    'maxRange',
    'Maximum range must be a valid non-negative number'
  );

  return {
    values,
    errors: Object.keys(errors).length === 0 ? undefined : errors,
  };
};
